import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BreadcrumbComponent } from '@alfresco/adf-content-services';
import { Node, PathElement, PathInfo } from '@alfresco/js-api';

import { Store } from '@ngrx/store';
import { AppStore } from '../../store/states/app.state';
import {
  currentSiteDocumentRefId,
  sitesSelector,
} from '../../store/selectors/app.custom-selectors';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { WkSiteModel } from '../../model/wk-site.model';
import { WkSiteContainerModel } from '../../model/wk-site-children.model';
import { ContentService } from '../shared/services/content.service';
import { MatOptionSelectionChange } from '@angular/material/core';

@Component({
  selector: 'wk-breadcrumb',
  templateUrl: './wk-breadcrumb.component.html',
  styleUrls: ['./wk-breadcrumb.component.scss'],
  standalone: false,
})
export class WkBreadCrumbComponent
  extends BreadcrumbComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  readOnly = false;
  folderPath: PathInfo;
  protected onDestroyed$ = new Subject<void>();

  constructor(
    private readonly wkContentService: ContentService,
    private readonly customRouter: Router,
    private readonly store: Store<AppStore>
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  ngOnChanges() {
    this.getPathInfo();
  }

  ngOnDestroy() {
    this.onDestroyed$.next();
    this.onDestroyed$.complete();
  }

  breadcrumbItemIsAnchor(lastItem): boolean {
    return !this.readOnly && !lastItem;
  }

  getPathInfo() {
    if (this.folderNode?.path) {
      this.recalculateNodeInfo(this.folderNode);
    } else if (this.folderNode && !this.folderNode.path) {
      this.wkContentService
        .getNode(this.folderNode.id)
        .subscribe((data) => this.recalculateNodeInfo(data['entry']));
    }
  }

  hasPreviousNodes(): boolean {
    return this.previousNodes && this.previousNodes.length > 0;
  }

  recalculateNodeInfo(node: Node): void {
    const cloneNode = JSON.parse(JSON.stringify(node));
    this.prepareRoutes(cloneNode).subscribe(() => {
      this.lastNodes = this.route.slice(this.route.length - this.maxItems);
      this.previousNodes = this.route.slice(
        0,
        this.route.length - this.maxItems
      );

      this.previousNodes.reverse();
    });
  }

  getPathElementName() {
    return this.customRouter.url.includes('favorite')
      ? 'APP.BROWSE.LIBRARIES.MENU.FAVORITE_LIBRARIES.TITLE'
      : 'APP.BROWSE.LIBRARIES.MENU.MY_LIBRARIES.TITLE';
  }

  private chkForContainers(parentIndex: number, routes: PathElement[]) {
    return routes[parentIndex].nodeType === 'st:site';
  }

  /**
   * Checks the path of the node and then updates the path by removing Sites and document library folder
   * from the path and assign the value of document library id to the site instead. For other containers it
   * won't behave the same way which needs to be standardized in the future implementation.
   * @param node node to prepare routes
   * @returns an Observable of either a boolean or an array of WkSiteContainerModel based on the folder opened.
   */
  prepareRoutes(node: Node): Observable<any> {
    const rawRoutes = this.parseRoute(node);
    let documentLibraryId;
    let templateLibraryId;
    // updating logic to get the value from store instead of an API
    return this.store.select(sitesSelector).pipe(
      switchMap((sites: WkSiteModel[]) => {
        this.route = rawRoutes
          .map((pathElement, index) => {
            if (pathElement.name === 'Company Home') {
              pathElement.name = this.getPathElementName();
            }
            //needs to be standardized for both documentLibrary and other containers
            if (pathElement.name === 'documentLibrary') {
              documentLibraryId = pathElement.id;
            } else if (
              index - 1 !== -1 &&
              this.chkForContainers(index - 1, rawRoutes)
            ) {
              templateLibraryId = pathElement.id;
            }
            if (pathElement.nodeType === 'st:site') {
              const site = sites.find((el) => el.id === pathElement.name);
              pathElement.name = site?.title || pathElement.name;
            }
            return pathElement;
          })
          .filter(
            (pathElement) =>
              pathElement.name !== 'Sites' &&
              pathElement.name !== 'documentLibrary'
          );
        if (templateLibraryId) {
          // fetch current site containers instead of getting value from the source.
          return this.store.select(currentSiteDocumentRefId);
        }
        if (documentLibraryId) {
          this.route[1].id = documentLibraryId;
        }
        return of(true);
      }),
      filter(
        (containersOrDocumentLibrary: WkSiteContainerModel[] | boolean) =>
          !!containersOrDocumentLibrary
      ),
      tap((containersOrDocumentLibrary: boolean | WkSiteContainerModel[]) => {
        if (containersOrDocumentLibrary instanceof Array) {
          const templateElementIndex = this.route.findIndex(
            (el) => el.id === templateLibraryId
          );
          const containerNode = containersOrDocumentLibrary.find(
            (element: WkSiteContainerModel) => element.id === templateLibraryId
          );
          this.route[templateElementIndex].name =
            containerNode.title || containerNode.name;
        }
      }),
      takeUntil(this.onDestroyed$)
    );
  }

  onRoutePathClick(
    route: PathElement,
    event?: Event | MatOptionSelectionChange
  ): void {
    if (event && (event as Event).type === 'click') {
      (event as Event).preventDefault();
    }
    if (route && !this.readOnly) {
      this.navigate.emit(route);
    }
  }
}
